import {
  documentToReactComponents,
  NodeRenderer,
  Options,
} from '@contentful/rich-text-react-renderer';
import { Link } from '@/components/ui/link';
import { BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import {
  ColoradoPdfContent,
  CoReplacementReason,
} from '@/app/(portal)/confirmation/print/state-specific-pdf-content/colorado-pdf-content';
import dayjs from 'dayjs';

const renderEmbeddedEntry: NodeRenderer = (node) => {
  const { fields, sys } = node?.data?.target;
  const contentType = sys.contentType.sys.id;

  switch (contentType) {
    case 'flexColumns': {
      return (
        <div className="flex flex-wrap grow flex-col">
          {Object.keys(fields).map((key) => {
            if (key.includes('column') && fields[key] !== undefined) {
              return (
                <div key={key} className="flex grow">
                  {documentToReactComponents(fields[key], renderOptions)}
                </div>
              );
            }
          })}
        </div>
      );
    }
    default:
      return null;
  }
};

export const renderOptions: Options = {
  renderText: (text) =>
    text
      .split('\n')
      .flatMap((v: any, i: number) => [i > 0 && <br key={i} />, v]),
  renderNode: {
    [BLOCKS.HEADING_3]: (_, children) => (
      <h3 className="text-4xl text-neutral-800 my-8 font-normal tracking-[-0.01rem] leading-[160%]">
        {children}
      </h3>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <h4 className="text-5xl text-neutral-800 mb-4 font-normal">{children}</h4>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <li className="list-item [&_p]:leading-[180%] [&_p]:tracking-tight">
        {children}
      </li>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul className="ml-8 list-disc">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol className="list-decimal list-outside pl-6">{children}</ol>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => <p className="pb-4">{children}</p>,
    [INLINES.HYPERLINK]: (_, children) => (
      <Link
        href={_.data.uri}
        className="text-blue-900 underline hover:no-underline"
      >
        {children}
      </Link>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedEntry,
    [INLINES.EMBEDDED_ENTRY]: (_) => null,
  },
};

const renderDialogEmbeddedEntry: NodeRenderer = (node) => {
  const { fields, sys } = node?.data?.target;
  const contentType = sys.contentType.sys.id;

  switch (contentType) {
    case 'flexColumns': {
      return (
        <div className="flex flex-wrap grow flex-col text-blue-900">
          {Object.keys(fields).map((key) => {
            if (key.includes('column') && fields[key] !== undefined) {
              return (
                <div key={key} className="flex grow">
                  {documentToReactComponents(fields[key], renderOptions)}
                </div>
              );
            }
          })}
        </div>
      );
    }
    default:
      return null;
  }
};
export const dialogRenderOptions: Options = {
  renderText: (text) =>
    text
      .split('\n')
      .flatMap((v: any, i: number) => [i > 0 && <br key={i} />, v]),
  renderNode: {
    [BLOCKS.HEADING_3]: (_, children) => (
      <h3 className="heading-lg border-b-1 border-olive-400">{children}</h3>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <li className="text-blue-900">{children}</li>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul className="list-disc list-outside">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol className="list-decimal text-blue-900 list-outside px-7">
        {children}
      </ol>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => <p className="body-lg">{children}</p>,
    [BLOCKS.EMBEDDED_ENTRY]: renderDialogEmbeddedEntry,
    [INLINES.EMBEDDED_ENTRY]: (_) => null,
  },
};

export const getInlineEmbeddedEntryId = (node: Inline): string | undefined => {
  return (node as any)?.data?.target?.fields?.id;
};

export const getPdfOptions = (applicantName: string): Options => ({
  ...renderOptions,
  renderNode: {
    [BLOCKS.UL_LIST]: (node: any, children: any) => (
      <ul className="ml-8 list-disc">{children}</ul>
    ),
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => (
      <p className="pb-4">{children}</p>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node: any) => {
      const entryId = getInlineEmbeddedEntryId(node);
      if (!entryId) {
        return null;
      }
      if (entryId === 'COReplacementQuestionnaire') {
        return <ColoradoPdfContent />;
      }
      if (entryId === 'applicantSignature') {
        return <span>Electronically signed by: {applicantName}</span>;
      }
      if (entryId === 'replacementQuestionDate') {
        return <span>Date: {dayjs().format('MM/DD/YYYY')}</span>;
      }
      if (entryId === 'coReplacementReason') {
        return <CoReplacementReason />;
      }
      return null;
    },
  },
});
